import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/roles";
import { GoogleSignIn } from "@/pages/auth/GoogleSignIn";
import { IMG } from "@/lib/images";
import { ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { TwoFactor } from "@/pages/auth/TwoFactor";

export const Shell = ({ children, title, sub }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="relative hidden lg:block">
      <img src={IMG.staff} alt="Teacher with children" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#0F172A]/60" />
      <div className="relative h-full flex flex-col justify-between p-12 text-white">
        <Link to="/" className="font-display text-2xl font-bold">Teach Play to Learn</Link>
        <div>
          <p className="font-display text-4xl font-bold leading-tight">Promoting a bright future with early learning.</p>
          <p className="mt-4 text-white/70">Little Rock, Arkansas · Ages 0–12</p>
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
      <div className="w-full max-w-md tpl-fade-up">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]"
              style={{ transition: "color .2s ease" }} data-testid="back-home-link">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <h1 className="mt-8 text-4xl font-bold tracking-tight">{title}</h1>
        <p className="mt-3 text-muted-foreground">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export function Login() {
  const { login, setSession } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav(homeFor(r.user.role));
    else if (r.mfa) setMfa(r.mfa);
    else setError(r.error);
  };

  const finish = (u) => { setSession(u); nav(homeFor(u.role)); };

  if (mfa)
    return (
      <Shell title="Check your email" sub="One more step keeps your family's information safe.">
        <TwoFactor mfa={mfa} onSuccess={finish} />
        <button type="button" onClick={() => setMfa(null)} className="mt-6 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]"
                data-testid="twofa-back">← Back to sign in</button>
      </Shell>
    );

  return (
    <Shell title="Welcome back" sub="Sign in to your staff or family portal.">
      {params.get("timeout") && (
        <p className="mt-6 text-sm font-semibold bg-amber-50 text-amber-800 rounded-2xl p-4" data-testid="timeout-notice">
          You were signed out after a period of inactivity. Please sign in again.
        </p>
      )}
      <div className="mt-8">
        <GoogleSignIn onSession={finish} onMfa={setMfa} />
        <div className="my-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or with email <span className="h-px flex-1 bg-border" />
        </div>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <Label className="text-sm font-bold">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username"
                 className="mt-2 h-12 rounded-xl" data-testid="login-email" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-sm font-bold">Password</Label>
            <Link to="/forgot-password" className="text-sm font-bold text-[#3B82F6]" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                 className="mt-2 h-12 rounded-xl" data-testid="login-password" />
        </div>
        {error && <p className="text-sm font-semibold text-[#EF4444]" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy}
                className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold active:scale-95"
                style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <p className="text-sm text-muted-foreground" data-testid="invite-only-note">
          New here? <Link to="/register" className="font-bold text-[#3B82F6]" data-testid="to-register-link">Create an account</Link>{" "}
          or call (501) 904-7103. If the office already has an account for you, just continue with Google above.
        </p>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register, setSession } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const finish = (u) => { setSession(u); nav(homeFor(u.role)); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await register(form);
    setBusy(false);
    if (r.ok) nav(homeFor(r.user.role));
    else if (r.mfa) setMfa(r.mfa);
    else setError(r.error);
  };

  if (mfa)
    return (
      <Shell title="Check your email" sub="One more step keeps your family's information safe.">
        <TwoFactor mfa={mfa} onSuccess={finish} />
      </Shell>
    );

  return (
    <Shell title="Create your account" sub="Sign up with Google or an email address and password.">
      <div className="mt-8">
        <GoogleSignIn onSession={finish} onMfa={setMfa} />
        <div className="my-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or with email <span className="h-px flex-1 bg-border" />
        </div>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <Label className="text-sm font-bold">Full name</Label>
          <Input required value={form.name} onChange={set("name")} className="mt-2 h-12 rounded-xl" data-testid="register-name" />
        </div>
        <div>
          <Label className="text-sm font-bold">Email</Label>
          <Input type="email" required value={form.email} onChange={set("email")} autoComplete="username"
                 className="mt-2 h-12 rounded-xl" data-testid="register-email" />
        </div>
        <div>
          <Label className="text-sm font-bold">Phone <span className="text-muted-foreground font-semibold">(optional)</span></Label>
          <Input value={form.phone} onChange={set("phone")} className="mt-2 h-12 rounded-xl" data-testid="register-phone" />
        </div>
        <div>
          <Label className="text-sm font-bold">Password</Label>
          <Input type="password" required value={form.password} onChange={set("password")} autoComplete="new-password"
                 className="mt-2 h-12 rounded-xl" data-testid="register-password" />
          <p className="mt-2 text-xs text-muted-foreground">At least 10 characters with an uppercase letter, a lowercase letter and a number.</p>
        </div>
        {error && <p className="text-sm font-semibold text-[#EF4444]" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy}
                className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold active:scale-95"
                style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="register-submit">
          {busy ? "Creating account…" : "Create account"}
        </Button>
        <div className="flex items-start gap-3 bg-blue-50 rounded-2xl p-4" data-testid="register-note">
          <span className="text-[#3B82F6] shrink-0 mt-0.5"><EnvelopeSimple size={20} weight="duotone" /></span>
          <p className="text-xs">A new account starts with no children attached. The office connects your child to your
            account once enrollment is confirmed — call (501) 904-7103 or text (501) 791-1919 if you need it sooner.</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="font-bold text-[#3B82F6]" data-testid="to-login-link">Sign in</Link>
        </p>
      </form>
    </Shell>
  );
}
